import React, { useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { RutwCountdown } from 'pages/campaigns/roll-up-to-win/rutw-countdown';
import { getCountdownValue } from 'utils/dateTime';
import { screenReaderOnly } from 'utils/style/screen-reader-only';

const formatNumber = (n: number) => `${n}`.padStart(2, '0');

interface ICountdownProps {
  endTime: number;
  className?: string;
  onFinish: () => void;
  useClockStyle?: boolean;
  isRutwCountdown?: boolean;
}

const ClockWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2rem;
  font-family: ${Styles.fontFamily.base};
  color: ${Styles.color.black};
`;

const ClockMinAndSec = styled.div`
  background-color: white;
  border-radius: ${Styles.borderRadius};
  font-weight: ${Styles.fontWeight.heavy};
  width: 50px;
  height: 55px;
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 1;
  padding-bottom: 0.2rem;
`;

const ClockColon = styled.div`
  height: 55px;
  padding: 0 0.2rem;
`;

const ScreenReader = styled.div`
  ${screenReaderOnly}
`;

const Countdown = ({
  endTime,
  className = '',
  onFinish,
  useClockStyle,
  isRutwCountdown,
}: ICountdownProps) => {
  const [nowTime, setNowTime] = useState(() => Date.now());

  const timeLeft = useMemo(() => endTime - nowTime, [endTime, nowTime]);

  const { days, hours, minutes, seconds, screenReaderSec, screenReaderMins } = useMemo(
    () => getCountdownValue(timeLeft),
    [timeLeft]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setNowTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // If it's the RUTW countdown, just show the 0:00:00 countdown state
  if (!isRutwCountdown && onFinish && timeLeft <= 0) {
    onFinish();
    return null;
  }

  // const formattedDay = formatNumber(days);
  const formattedHours = formatNumber(hours);
  const formattedMinutes = formatNumber(minutes);
  const formattedSeconds = formatNumber(seconds);
  const formattedScreenReaderMins = formatNumber(screenReaderMins);
  const formattedScreenReaderSec = formatNumber(screenReaderSec);

  // TODO: Maybe fix the formattedScreenReader days and hours??
  const screenOnlyContent = isRutwCountdown
    ? `${days}:${hours}:${formattedScreenReaderMins}`
    : `${formattedScreenReaderMins}:${formattedScreenReaderSec}`;

  const screenOnlyElement = (
    <ScreenReader role="timer" aria-live="assertive" aria-atomic="true">
      {screenOnlyContent}
    </ScreenReader>
  );

  let clockElement;

  if (isRutwCountdown) {
    clockElement = <RutwCountdown days={days} hours={formattedHours} minutes={formattedMinutes} />;
  } else if (useClockStyle) {
    clockElement = (
      <ClockWrapper data-testid="countdown-clock">
        <ClockMinAndSec data-testid="minutes">{formattedMinutes}</ClockMinAndSec>
        <ClockColon>:</ClockColon>
        <ClockMinAndSec data-testid="seconds">{formattedSeconds}</ClockMinAndSec>
      </ClockWrapper>
    );
  } else {
    clockElement = (
      <span className={className}>
        {formattedMinutes}:{formattedSeconds}
      </span>
    );
  }

  return (
    <>
      {clockElement}
      {screenOnlyElement}
    </>
  );
};

export default Countdown;
